import AllBrandsSearch from "src/organisms/AllBrandsSearch";
import {
  GetStaticPathsResult,
  GetStaticPropsContext,
  GetStaticPropsResult,
} from "next/types";
import { ProductCategoryView } from "src/organisms/ProductCategoryView";
import {
  kidsGenderArr,
  kidsSlugs,
  allBrandsTranslations,
  genderTranslations,
} from "src/utils/routeTranslations";
import { CATEGORIES, PRODUCTS } from "src/utils/algolia";
import { AlgoliaCategory } from "src/types/Algolia";
import algoliasearch from "algoliasearch";
import getConfig from "next/config";
import {
  getServerState,
  InstantSearchServerState,
} from "react-instantsearch-core-v7";
import { renderToString } from "react-dom/server";
import { ListingConfiguration } from "src/organisms/ProductList/ListingConfiguration";
import { TranslationProvider } from "src/providers/TranslationProvider";
import { CategoryTreeProvider } from "src/contexts/category-tree-context";
import { PLPType } from "src/types/PLPType.d";
import { getNavigationItems } from "services/get-navigation-items";
import { NavigationItems, ShopType } from "src/types/graphql.d";
import { localeFix } from "src/utils/genderLocale/genderLocale";

type BrandOrCategoriesPageProps = {
  gender?: string | string[];
  slug?: string | string[];
  title?: string | null;
  isAllBrandsPage?: boolean;
  categoryPageId?: string | null;
  indexName: string;
  locale: string;
  navigationItems?: NavigationItems | null;
  breadcrumb: { title: string; slug?: string }[];
  search: {
    appId: string;
    searchKey: string;
  };
  serverState?: InstantSearchServerState;
  categoryId?: string;
};

export default function BrandOrCategoriesPage({
  pageProps: {
    indexName,
    categoryPageId,
    breadcrumb,
    gender,
    isAllBrandsPage,
    title,
    slug,
    search,
    locale,
    serverState,
    navigationItems,
    categoryId,
  },
}: {
  pageProps: BrandOrCategoriesPageProps;
}): JSX.Element {
  if (isAllBrandsPage) {
    return <AllBrandsSearch genderId={gender as string} locale={locale} />;
  }

  return (
    <ProductCategoryView
      gender={gender as string}
      slug={slug as string}
      title={title || ""}
      categoryPageId={categoryPageId as string}
      indexName={indexName}
      breadcrumb={breadcrumb}
      search={search}
      locale={locale}
      serverState={serverState}
      pageType={PLPType.CATEGORY_PLP}
      navigationItems={navigationItems}
      categoryId={categoryId}
    />
  );
}

export function getStaticPaths(): GetStaticPathsResult {
  return {
    paths: [],
    fallback: "blocking",
  };
}

export async function getStaticProps(
  context: GetStaticPropsContext<{
    slug: string;
    brandOrCategoriesSlug: string;
  }>
): Promise<GetStaticPropsResult<BrandOrCategoriesPageProps>> {
  if (
    !context.params ||
    !context.params.slug ||
    !context.params.brandOrCategoriesSlug
  ) {
    return {
      notFound: true,
    };
  }

  const { serverRuntimeConfig } = getConfig();

  const isAllBrands = allBrandsTranslations.includes(
    context.params?.brandOrCategoriesSlug || ""
  );

  if (isAllBrands) {
    return {
      props: {
        locale: serverRuntimeConfig.LOCALE,
        gender: context.params?.slug,
        slug: context.params?.brandOrCategoriesSlug,
        isAllBrandsPage: isAllBrands,
        indexName: `${serverRuntimeConfig.LOCALE}_${PRODUCTS}`,
        breadcrumb: [],
        search: {
          appId: process.env.ALGOLIA_APP_ID,
          searchKey: process.env.ALGOLIA_SEARCH_API_KEY,
        },
      },
    };
  }

  const isValidGender = genderTranslations.includes(context.params.slug);

  if (!isValidGender) {
    return {
      notFound: true,
    };
  }

  const searchClient = algoliasearch(
    process.env.ALGOLIA_APP_ID,
    process.env.ALGOLIA_SEARCH_API_KEY
  );

  const searchIndex = searchClient.initIndex(
    `${serverRuntimeConfig.LOCALE}_${CATEGORIES}`
  );

  const { hits } = await searchIndex.search<AlgoliaCategory>("", {
    filters: `slug: ${context.params?.brandOrCategoriesSlug || ""}`,
  });

  const hit = hits.find(
    (hit) => hit.slug === (context.params?.brandOrCategoriesSlug || "")
  );

  if (!hit) {
    return {
      notFound: true,
    };
  }

  let parentCategory: AlgoliaCategory | undefined;

  if (hit.parent) {
    const { hits: _parentCategory } = await searchIndex.search<AlgoliaCategory>(
      "",
      {
        filters: `objectID: ${hit?.parent || ""}`,
      }
    );

    parentCategory = _parentCategory.at(0);
  }

  const { navigationItems } =
    (await getNavigationItems(
      localeFix({ gender: context.params.slug }) as ShopType
    )) || [];

  const title = hit.name;
  const categoryPageId = hit.category_page_id?.at(-1);
  const breadcrumb: { title: string; slug?: string }[] = [
    { title: "Home", slug: "" },
    { title: hit.name },
  ];

  if (parentCategory) {
    breadcrumb.splice(1, 0, {
      title: parentCategory?.name,
      slug: `/${parentCategory?.slug}`,
    });
  }

  // kids page will be redirected to popular category page
  // however only few urls combinactions do, it can't be dynamic
  // thats because the legacy url structure is not consistent
  // for instance for english locale
  // kids-en/boys-en (goes to popular cateory page) but
  // kids-en/shirts (goes to listing page)
  // the gender slug needs to be checked by values to determine routing
  const isKidsGender =
    context.params &&
    kidsGenderArr.includes(context.params.slug) &&
    kidsSlugs.includes(context.params.brandOrCategoriesSlug);

  const serverState = await getServerState(
    <TranslationProvider>
      <CategoryTreeProvider>
        <ListingConfiguration
          categoryPageId={categoryPageId || ""}
          gender={context.params?.slug || ""}
          hitsPerPage={72}
          indexName={`${serverRuntimeConfig.LOCALE}_${PRODUCTS}`}
          searchClient={searchClient}
          pageType={PLPType.CATEGORY_PLP}
        />
      </CategoryTreeProvider>
    </TranslationProvider>,
    { renderToString }
  );

  // TEMP: We will revalidate the cache every 15min temporally.
  // Once we have backend support we will revalidate on demand.
  return {
    revalidate: 60 * 15,
    props: {
      locale: serverRuntimeConfig.LOCALE,
      gender: isKidsGender ? "kids" : context.params?.slug,
      slug: context.params?.brandOrCategoriesSlug,
      isAllBrandsPage: isAllBrands,
      title,
      categoryPageId,
      categoryId: hit.objectID,
      indexName: `${serverRuntimeConfig.LOCALE}_${PRODUCTS}`,
      breadcrumb,
      navigationItems,
      search: {
        appId: process.env.ALGOLIA_APP_ID,
        searchKey: process.env.ALGOLIA_SEARCH_API_KEY,
      },
      serverState,
    },
  };
}
